// VWO Apply Link A/B Test Utility
// This handles the Apply Link interstitial form experiment

import { getStableFmeId } from "./fmeIdentity";


/**
 * Get VWO SmartCode UUID for event tracking (all pages)
 * This is separate from FME UUID to avoid cross-contamination
 * @returns {string} VWO SmartCode user ID
 */
const getVWOSmartCodeUserId = () => {
  // SSR protection - return fallback ID during build/SSR
  if (typeof window === 'undefined') {
    return 'ssr_user_' + Date.now();
  }
  
  try {
    // Check if VWO SmartCode is available and has generated a UUID
    if (window.VWO && window.VWO._.allSettings && window.VWO._.allSettings.dataStore) {
      const vwoUuid = window.VWO._.allSettings.dataStore.plugins?.dacdnconfig?.uuid;
      if (vwoUuid) {
        return vwoUuid;
      }
    }
    
    // Alternative: Check VWO visitor UUID from data object
    if (window.VWO && window.VWO.data) {
      // Check vin.uuid (visitor identification)
      if (window.VWO.data.vin && window.VWO.data.vin.uuid) {
        return window.VWO.data.vin.uuid;
      }
      
      // Fallback to vi object if it exists
      if (window.VWO.data.vi) {
        const visitorId = window.VWO.data.vi.uuid || window.VWO.data.vi.vwoUserId;
        if (visitorId) {
          return visitorId;
        }
      }
    }
    
    // Check VWO cookies for UUID
    const vwoCookies = document.cookie
      .split(';')
      .map(cookie => cookie.trim())
      .filter(cookie => cookie.startsWith('_vwo_uuid'));
    
    if (vwoCookies.length > 0) {
      const vwoUuid = vwoCookies[0].split('=')[1];
      return vwoUuid;
    }
    
    // Fallback: Generate temporary ID that will be replaced when VWO initializes
    return 'temp_user_' + Date.now();
    
  } catch (error) {
    console.error('🎯 VWO: Error retrieving internal UUID for tracking:', error);
    return 'error_user_' + Date.now();
  }
};

/**
 * Get VWO FME UUID for A/B test decisions (whitelisted pages only)
 * Now uses the stable FME ID from fmeIdentity.js for better persistence
 * @returns {string} VWO FME user ID
 */
export const getVWOFMEUserId = () => {
  const fmeId = getStableFmeId();
  
  // During SSR, getStableFmeId returns null, so provide fallback
  if (!fmeId) {
    return 'fme_ssr_user_' + Date.now();
  }
  
  return fmeId;
};

/**
 * Get or create a consistent user ID for SmartCode event tracking
 * Uses separate UUID from FME to avoid cross-contamination between systems
 * @param {Object} arrivalInfo - Arrival info with potential sessionId  
 * @returns {string} - SmartCode user ID for event tracking
 */
export const getSessionUserId = (arrivalInfo) => {
  // Try to get from arrivalInfo first (for backwards compatibility)
  if (arrivalInfo?.sessionId) {
    return arrivalInfo.sessionId;
  }
  
  // Use VWO SmartCode UUID for event tracking (separate from FME)
  return getVWOSmartCodeUserId();
};

/**
 * VWO FME Apply Link A/B Test
 * Uses VWO Feature Management & Experimentation to control variations
 * Flag Key: applyNowFormInterstitial
 * Variable: show_interstitial (boolean)
 * Variant: showFormInterstitial
 * Control: Direct redirect to Findigs application  
 * Treatment: Show interstitial form first, then redirect
 */
export const getApplyLinkVariation = (userId) => {
  // Development override via URL parameter
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    const urlParams = new URLSearchParams(window.location.search);
    const forceVariant = urlParams.get('vwo_force_variant');
    
    if (forceVariant === 'treatment' || forceVariant === 'control') {
      console.log(`🔧 DEV OVERRIDE: Forcing ${forceVariant} variant via URL parameter`);
      return forceVariant;
    }
  }
  
  if (typeof window !== 'undefined' && window.VWO) {
    // Check if VWO FME is available (has getFlag method)
    if (window.VWO.getFlag) {
      try {
        // Use VWO FME to check if flag is enabled and get variable value
        const flagEnabled = window.VWO.getFlag('applyNowFormInterstitial');
        
        if (flagEnabled && window.VWO.getVariable) {
          const showInterstitial = window.VWO.getVariable('show_interstitial', false);
          const variation = showInterstitial ? 'treatment' : 'control';
          
          console.log('VWO FME - Apply Link Variation:', variation, 'flag enabled:', flagEnabled, 'show_interstitial:', showInterstitial, 'for session:', userId);
          return variation;
        }
        
        console.log('VWO FME - Flag not enabled, defaulting to control');
        return 'control';
      } catch (error) {
        console.error('VWO FME Apply Test Error:', error);
        return 'control'; // Default fallback
      }
    } else {
      // VWO SmartCode is loaded but FME not yet ready
      console.log('VWO SmartCode loaded, but FME not ready yet');
      return 'control';
    }
  }
  
  return 'control'; // Default when VWO not loaded
};


/**
 * Track streamlined Apply Link conversion events
 * Uses simplified event structure with position as custom attribute
 * @param {string} eventName - Core event name (button_clicked, control_redirect, form_displayed, etc.)
 * @param {string} userId - User identifier
 * @param {string} propertySlug - Property slug identifier
 * @param {string} buttonPosition - Button position (header, floorplans, units, map, cta, unknown)
 * @param {Object} additionalData - Additional event properties
 */
export const trackApplyLinkEvent = (eventName, userId, propertySlug, buttonPosition = 'unknown', additionalData = {}) => {
  // Map old event names to new streamlined events
  const eventMapping = {
    'button_clicked': 'apply_button_clicked',
    'direct_redirect': 'apply_control_redirect',
    'direct_redirect_repeat': 'apply_repeat_user',
    'form_displayed': 'apply_form_displayed',
    'form_submitted': 'apply_form_completed',
    'form_skipped': 'apply_form_abandoned',
    'form_error': 'apply_form_error', 
    'interstitial_completed': 'apply_treatment_redirect'
  };
  
  const mappedEventName = eventMapping[eventName] || `apply_${eventName}`;
  
  // VWO Event Tracking
  if (typeof window !== 'undefined' && window.VWO) {
    try {
      // Custom event tracking with position as attribute instead of event name
      window.VWO.event = window.VWO.event || function () {
        window.VWO.push(["event"].concat([].slice.call(arguments)));
      };
      
      window.VWO.event(mappedEventName, {
        property: propertySlug,
        userId: userId,
        button_position: buttonPosition,
        original_event: eventName,
        campaign_type: 'fme_apply_interstitial', // Namespace FME events
        tracking_source: 'smartcode', // Distinguish from pure FME events
        ...additionalData
      });
      
      console.log(`🎯 VWO SmartCode Event Tracked: ${mappedEventName}`, {
        property: propertySlug,
        button_position: buttonPosition,
        fme_userId: userId, // FME UUID (now consistent with URL tracking)
        campaign_type: 'fme_apply_interstitial',
        ...additionalData
      });
    } catch (error) {
      console.error('VWO Tracking Error:', error);
    }
  }
  
  // Console logging for development
  if (process.env.NODE_ENV === 'development') {
    console.log(`📊 Apply Button Event: ${mappedEventName}`, {
      property: propertySlug,
      button_position: buttonPosition,
      action: eventName,
      userId: userId,
      ...additionalData
    });
  }
};

